import { SudokuGenerator } from "./sudoku.js";
import "./styles.css";

// Sudoku PWA App
class SudokuApp {
  constructor() {
    this.grid = [];
    this.solution = [];
    this.fixed = [];
    this.notes = [];
    this.history = [];
    this.selectedCell = null;
    this.notesMode = false;
    this.difficulty = "medium";
    this.timerInterval = null;
    this.seconds = 0;
    this.gameWon = false;
    this.mistakes = 0;
    this.hintsUsed = 0;
    this.score = 0;

    this.settings = {
      highlightErrors: true,
      highlightSame: true,
      highlightRowCol: true,
      showRemaining: true,
      theme: "default",
    };

    this.bestScores = {
      easy: 0,
      medium: 0,
      hard: 0,
    };

    this.init();
  }

  init() {
    this.loadSettings();
    this.loadBestScores();
    this.createGrid();
    this.bindEvents();
    this.tryResumeGame();
  }

  // Save current game state to localStorage
  saveGameState() {
    if (this.gameWon || !this.grid.length) return;

    const state = {
      grid: this.grid,
      solution: this.solution,
      fixed: this.fixed,
      notes: this.notes.map((row) => row.map((cell) => Array.from(cell))),
      difficulty: this.difficulty,
      seconds: this.seconds,
      mistakes: this.mistakes,
      hintsUsed: this.hintsUsed,
      selectedCell: this.selectedCell,
      notesMode: this.notesMode,
    };

    localStorage.setItem("sudoku-game-state", JSON.stringify(state));
  }

  // Load saved game state from localStorage
  loadGameState() {
    const saved = localStorage.getItem("sudoku-game-state");
    if (!saved) return false;

    try {
      const state = JSON.parse(saved);

      this.grid = state.grid;
      this.solution = state.solution;
      this.fixed = state.fixed;
      this.notes = state.notes.map((row) => row.map((cell) => new Set(cell)));
      this.difficulty = state.difficulty;
      this.seconds = state.seconds;
      this.mistakes = state.mistakes;
      this.hintsUsed = state.hintsUsed;
      this.selectedCell = state.selectedCell;
      this.notesMode = state.notesMode;
      this.history = [];
      this.gameWon = false;
      this.score = 0;

      return true;
    } catch (e) {
      console.error("Failed to load game state:", e);
      this.clearGameState();
      return false;
    }
  }

  // Clear saved game state
  clearGameState() {
    localStorage.removeItem("sudoku-game-state");
  }

  // Check if there's a saved game
  hasSavedGame() {
    return localStorage.getItem("sudoku-game-state") !== null;
  }

  // Try to resume a saved game on app start
  tryResumeGame() {
    if (this.hasSavedGame() && this.loadGameState()) {
      document.getElementById("difficulty-label").textContent = this.difficulty;
      document
        .getElementById("notes-btn")
        .classList.toggle("active", this.notesMode);

      this.showScreen("game-screen");
      this.renderGrid();
      this.updateNumberPad();
      this.resumeTimer();
    }
  }

  // Resume timer from saved seconds
  resumeTimer() {
    this.stopTimer();
    this.updateTimerDisplay();
    this.updateLiveScore();
    this.timerInterval = setInterval(() => {
      this.seconds++;
      this.updateTimerDisplay();
      this.updateLiveScore();
      this.saveGameState();
    }, 1000);
  }

  loadBestScores() {
    const saved = localStorage.getItem("sudoku-best-scores");
    if (saved) {
      this.bestScores = { ...this.bestScores, ...JSON.parse(saved) };
    }
  }

  saveBestScore(difficulty, score) {
    if (score > this.bestScores[difficulty]) {
      this.bestScores[difficulty] = score;
      localStorage.setItem(
        "sudoku-best-scores",
        JSON.stringify(this.bestScores),
      );
      return true;
    }
    return false;
  }

  loadSettings() {
    const saved = localStorage.getItem("sudoku-settings");
    if (saved) {
      this.settings = { ...this.settings, ...JSON.parse(saved) };
    }
    this.applySettings();
  }

  saveSettings() {
    localStorage.setItem("sudoku-settings", JSON.stringify(this.settings));
  }

  applySettings() {
    document.getElementById("highlight-errors").checked =
      this.settings.highlightErrors;
    document.getElementById("highlight-same").checked =
      this.settings.highlightSame;
    document.getElementById("highlight-row-col").checked =
      this.settings.highlightRowCol;
    document.getElementById("show-remaining").checked =
      this.settings.showRemaining;
    document.getElementById("theme-selector").value = this.settings.theme;
    this.applyTheme(this.settings.theme);
  }

  applyTheme(theme) {
    if (theme === "default") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", theme);
    }
  }

  createGrid() {
    const gridEl = document.getElementById("sudoku-grid");
    gridEl.innerHTML = "";

    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        const cell = document.createElement("div");
        cell.className = "cell";
        cell.dataset.row = r;
        cell.dataset.col = c;
        gridEl.appendChild(cell);
      }
    }
  }

  bindEvents() {
    // Difficulty buttons
    document.querySelectorAll(".difficulty-buttons .btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.difficulty = btn.dataset.difficulty;
        this.startGame();
      });
    });

    // Grid cells
    document.getElementById("sudoku-grid").addEventListener("click", (e) => {
      const cell = e.target.closest(".cell");
      if (cell) {
        this.selectCell(parseInt(cell.dataset.row), parseInt(cell.dataset.col));
      }
    });

    // Number pad
    document.querySelectorAll(".num-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (this.selectedCell && !this.gameWon) {
          const num = parseInt(btn.dataset.num);
          this.enterNumber(num);
        }
      });
    });

    // Action buttons
    document
      .getElementById("undo-btn")
      .addEventListener("click", () => this.undo());
    document
      .getElementById("hint-btn")
      .addEventListener("click", () => this.getHint());
    document
      .getElementById("notes-btn")
      .addEventListener("click", () => this.toggleNotesMode());

    // Menu buttons
    document
      .getElementById("menu-btn")
      .addEventListener("click", () => this.showModal("menu-modal"));
    document
      .getElementById("resume-btn")
      .addEventListener("click", () => this.hideModal("menu-modal"));
    document.getElementById("new-game-btn").addEventListener("click", () => {
      this.hideModal("menu-modal");
      this.showScreen("start-screen");
      this.stopTimer();
    });

    // Settings
    document
      .getElementById("settings-btn")
      .addEventListener("click", () => this.showModal("settings-modal"));
    document.getElementById("close-settings").addEventListener("click", () => {
      this.hideModal("settings-modal");
      this.saveSettings();
    });

    // Settings checkboxes
    document
      .getElementById("highlight-errors")
      .addEventListener("change", (e) => {
        this.settings.highlightErrors = e.target.checked;
        this.renderGrid();
      });
    document
      .getElementById("highlight-same")
      .addEventListener("change", (e) => {
        this.settings.highlightSame = e.target.checked;
        this.renderGrid();
      });
    document
      .getElementById("highlight-row-col")
      .addEventListener("change", (e) => {
        this.settings.highlightRowCol = e.target.checked;
        this.renderGrid();
      });
    document
      .getElementById("show-remaining")
      .addEventListener("change", (e) => {
        this.settings.showRemaining = e.target.checked;
        this.updateNumberPad();
      });
    document
      .getElementById("theme-selector")
      .addEventListener("change", (e) => {
        this.settings.theme = e.target.value;
        this.applyTheme(this.settings.theme);
      });

    // Win modal
    document.getElementById("play-again-btn").addEventListener("click", () => {
      this.hideModal("win-modal");
      this.showScreen("start-screen");
    });

    // Keyboard support
    document.addEventListener("keydown", (e) => {
      if (this.selectedCell && !this.gameWon) {
        if (e.key >= "1" && e.key <= "9") {
          this.enterNumber(parseInt(e.key));
        } else if (
          e.key === "Backspace" ||
          e.key === "Delete" ||
          e.key === "0"
        ) {
          this.enterNumber(0);
        } else if (e.key === "n" || e.key === "N") {
          this.toggleNotesMode();
        }
      }
    });
  }

  startGame() {
    this.clearGameState();

    const generator = new SudokuGenerator();
    const { puzzle, solution } = generator.generate(this.difficulty);

    this.grid = puzzle.map((row) => [...row]);
    this.solution = solution;
    this.fixed = puzzle.map((row) => row.map((cell) => cell !== 0));
    this.notes = Array(9)
      .fill(null)
      .map(() =>
        Array(9)
          .fill(null)
          .map(() => new Set()),
      );
    this.history = [];
    this.selectedCell = null;
    this.notesMode = false;
    this.gameWon = false;
    this.seconds = 0;
    this.mistakes = 0;
    this.hintsUsed = 0;
    this.score = 0;

    document.getElementById("difficulty-label").textContent = this.difficulty;
    document.getElementById("notes-btn").classList.remove("active");

    this.showScreen("game-screen");
    this.renderGrid();
    this.updateNumberPad();
    this.startTimer();
    this.saveGameState();
  }

  showScreen(screenId) {
    document
      .querySelectorAll(".screen")
      .forEach((s) => s.classList.remove("active"));
    document.getElementById(screenId).classList.add("active");
  }

  showModal(modalId) {
    document.getElementById(modalId).classList.add("active");
  }

  hideModal(modalId) {
    document.getElementById(modalId).classList.remove("active");
  }

  selectCell(row, col) {
    this.selectedCell = { row, col };
    this.renderGrid();
  }

  enterNumber(num) {
    if (!this.selectedCell) return;
    const { row, col } = this.selectedCell;

    if (this.fixed[row][col]) return;

    if (this.notesMode && num !== 0) {
      // Toggle note
      const notes = this.notes[row][col];
      if (notes.has(num)) {
        notes.delete(num);
      } else {
        notes.add(num);
      }
      // Clear the cell value when adding notes
      if (this.grid[row][col] !== 0) {
        this.history.push({
          type: "number",
          row,
          col,
          oldValue: this.grid[row][col],
          newValue: 0,
        });
        this.grid[row][col] = 0;
      }
    } else {
      // Save to history
      if (this.grid[row][col] !== num) {
        this.history.push({
          type: "number",
          row,
          col,
          oldValue: this.grid[row][col],
          newValue: num,
          oldNotes: new Set(this.notes[row][col]),
        });
      }

      // Set number
      this.grid[row][col] = num;
      this.notes[row][col].clear();

      // Track mistakes
      if (num !== 0 && num !== this.solution[row][col]) {
        this.mistakes++;
        this.updateLiveScore();
      }

      // Remove this number from notes in same row, col, box
      if (num !== 0) {
        this.removeNoteFromRelated(row, col, num);
      }
    }

    this.renderGrid();
    this.updateNumberPad();
    this.saveGameState();
    this.checkWin();
  }

  removeNoteFromRelated(row, col, num) {
    // Row
    for (let c = 0; c < 9; c++) {
      this.notes[row][c].delete(num);
    }

    // Column
    for (let r = 0; r < 9; r++) {
      this.notes[r][col].delete(num);
    }

    // Box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        this.notes[boxRow + r][boxCol + c].delete(num);
      }
    }
  }

  undo() {
    if (this.history.length === 0) return;

    const action = this.history.pop();
    if (action.type === "number") {
      this.grid[action.row][action.col] = action.oldValue;
      if (action.oldNotes) {
        this.notes[action.row][action.col] = action.oldNotes;
      }
    }

    this.renderGrid();
    this.updateNumberPad();
    this.saveGameState();
  }

  getHint() {
    const hint = SudokuGenerator.getHint(this.grid, this.solution, this.fixed);
    if (hint) {
      this.hintsUsed++;
      this.updateLiveScore();
      this.selectCell(hint.row, hint.col);
      this.notesMode = false;
      document.getElementById("notes-btn").classList.remove("active");
      this.enterNumber(hint.num);
    }
  }

  toggleNotesMode() {
    this.notesMode = !this.notesMode;
    document
      .getElementById("notes-btn")
      .classList.toggle("active", this.notesMode);
  }

  renderGrid() {
    const cells = document.querySelectorAll(".cell");
    const selectedNum = this.selectedCell
      ? this.grid[this.selectedCell.row][this.selectedCell.col]
      : 0;

    cells.forEach((cell) => {
      const row = parseInt(cell.dataset.row);
      const col = parseInt(cell.dataset.col);
      const value = this.grid[row][col];
      const isFixed = this.fixed[row][col];
      const isSelected =
        this.selectedCell &&
        this.selectedCell.row === row &&
        this.selectedCell.col === col;

      // Reset classes
      cell.className = "cell";
      if (isFixed) cell.classList.add("fixed");
      if (!isFixed && value !== 0) cell.classList.add("user");
      if (isSelected) cell.classList.add("selected");

      // Highlight related cells
      if (this.selectedCell && this.settings.highlightRowCol && !isSelected) {
        const sameRow = this.selectedCell.row === row;
        const sameCol = this.selectedCell.col === col;
        const sameBox =
          Math.floor(this.selectedCell.row / 3) === Math.floor(row / 3) &&
          Math.floor(this.selectedCell.col / 3) === Math.floor(col / 3);

        if (sameRow || sameCol || sameBox) {
          cell.classList.add("highlighted");
        }
      }

      // Highlight same numbers
      if (
        this.settings.highlightSame &&
        selectedNum !== 0 &&
        value === selectedNum &&
        !isSelected
      ) {
        cell.classList.add("same-number");
      }

      // Highlight errors
      if (this.settings.highlightErrors && value !== 0 && !isFixed) {
        if (SudokuGenerator.hasConflict(this.grid, row, col, value)) {
          cell.classList.add("error");
        }
      }

      // Render content
      if (value !== 0) {
        cell.textContent = value;
      } else if (this.notes[row][col].size > 0) {
        cell.innerHTML = "";
        const notesDiv = document.createElement("div");
        notesDiv.className = "notes";
        for (let n = 1; n <= 9; n++) {
          const span = document.createElement("span");
          span.textContent = this.notes[row][col].has(n) ? n : "";
          notesDiv.appendChild(span);
        }
        cell.appendChild(notesDiv);
      } else {
        cell.textContent = "";
      }
    });
  }

  updateNumberPad() {
    const remaining = SudokuGenerator.getRemainingCounts(this.grid);

    document.querySelectorAll(".num-btn").forEach((btn) => {
      const num = parseInt(btn.dataset.num);
      if (num === 0) return;

      const count = remaining[num];
      btn.classList.toggle("disabled", count === 0);

      // Show remaining count
      let badge = btn.querySelector(".remaining");
      if (this.settings.showRemaining && count > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "remaining";
          btn.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  checkWin() {
    // Check if all cells are filled
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (this.grid[r][c] === 0) return;
      }
    }

    // Validate solution
    if (SudokuGenerator.validate(this.grid, this.solution)) {
      this.gameWon = true;
      this.stopTimer();
      this.clearGameState();

      setTimeout(() => {
        this.showWinScreen();
        this.startConfetti();
      }, 300);
    }
  }

  showWinScreen() {
    const minutes = Math.floor(this.seconds / 60);
    const secs = this.seconds % 60;

    // Calculate score
    this.score = this.calculateScore();

    // Check for new best
    const isNewBest = this.saveBestScore(this.difficulty, this.score);
    const bestScore = this.bestScores[this.difficulty];

    document.getElementById("win-time").textContent =
      `Time: ${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

    const scoreEl = document.getElementById("win-score");
    if (isNewBest) {
      scoreEl.innerHTML = `<span class="new-best">🏆 NEW BEST!</span><br>Score: ${this.score}`;
    } else {
      scoreEl.innerHTML = `Score: ${this.score}<br><span class="best-score">Best: ${bestScore}</span>`;
    }

    this.showModal("win-modal");
  }

  calculateScore() {
    // Base score depends on difficulty
    const baseScore =
      {
        easy: 1000,
        medium: 2000,
        hard: 3000,
      }[this.difficulty] || 1000;

    // Time bonus: lose points for taking longer
    // Ideal time: easy=5min, medium=10min, hard=20min
    const idealTime =
      {
        easy: 300,
        medium: 600,
        hard: 1200,
      }[this.difficulty] || 600;

    const timeBonus = Math.max(0, Math.floor((idealTime - this.seconds) * 2));

    // Penalties
    const mistakePenalty = this.mistakes * 50;
    const hintPenalty = this.hintsUsed * 100;

    const finalScore = Math.max(
      0,
      baseScore + timeBonus - mistakePenalty - hintPenalty,
    );

    return finalScore;
  }

  startTimer() {
    this.stopTimer();
    this.seconds = 0;
    this.updateTimerDisplay();
    this.updateLiveScore();
    this.timerInterval = setInterval(() => {
      this.seconds++;
      this.updateTimerDisplay();
      this.updateLiveScore();
      this.saveGameState();
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  updateTimerDisplay() {
    const minutes = Math.floor(this.seconds / 60);
    const secs = this.seconds % 60;
    document.getElementById("timer").textContent =
      `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  updateLiveScore() {
    const currentScore = this.calculateScore();
    const scoreEl = document.getElementById("live-score");
    if (scoreEl) {
      scoreEl.textContent = `${currentScore} pts`;
    }
  }

  // Confetti Animation
  startConfetti() {
    const canvas = document.getElementById("confetti-canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const colors = [
      "#e94560",
      "#4fc3f7",
      "#2ecc71",
      "#f39c12",
      "#9b59b6",
      "#fff",
    ];
    const particleCount = 150;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        size: Math.random() * 10 + 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedY: Math.random() * 3 + 2,
        speedX: Math.random() * 4 - 2,
        rotation: Math.random() * 360,
        rotationSpeed: Math.random() * 10 - 5,
      });
    }

    let animationFrame;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allDone = true;
      particles.forEach((p) => {
        if (p.y < canvas.height + 50) {
          allDone = false;
          p.y += p.speedY;
          p.x += p.speedX;
          p.rotation += p.rotationSpeed;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size / 2);
          ctx.restore();
        }
      });

      if (!allDone) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animate();

    // Clean up after animation
    setTimeout(() => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 5000);
  }
}

// Initialize app
new SudokuApp();

// PWA Update Detection
(function setupUpdateNotification() {
  if (!('serviceWorker' in navigator)) return;

  let waitingWorker = null;
  const toast = document.getElementById('update-toast');
  const updateBtn = document.getElementById('update-btn');
  const dismissBtn = document.getElementById('dismiss-update');

  function showUpdateNotification(worker) {
    waitingWorker = worker;
    toast?.classList.remove('hidden');
  }

  function hideUpdateNotification() {
    toast?.classList.add('hidden');
  }

  // Handle update button click
  updateBtn?.addEventListener('click', () => {
    if (waitingWorker) {
      // Tell the waiting service worker to skip waiting and activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    hideUpdateNotification();
  });

  // Handle dismiss button click
  dismissBtn?.addEventListener('click', hideUpdateNotification);

  // Listen for controller change and reload
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });

  // Check for updates on existing registration
  navigator.serviceWorker.ready.then((registration) => {
    // Check if there's already a waiting worker
    if (registration.waiting) {
      showUpdateNotification(registration.waiting);
    }

    // Listen for new service worker installing
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        // When the new worker is installed and waiting
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          showUpdateNotification(newWorker);
        }
      });
    });
  });

  // Periodically check for updates (every 60 seconds)
  setInterval(() => {
    navigator.serviceWorker.ready.then((registration) => {
      registration.update();
    });
  }, 60000);
})();
